import {
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { HttpResponse } from '../../../application/types/HttpResponse';
import {
  DashboardUser,
  RefreshApiTokenResponse,
  UserRole,
} from '../../types/User';
import { UserManagementApiService } from '../../services/user-management-api/UserManagementApiService';

@Component({
  selector: 'app-profile-page',
  standalone: false,
  templateUrl: './profile-page.component.html',
  styleUrl: './profile-page.component.sass',
})
export class ProfilePageComponent implements OnInit {
  public me: DashboardUser | null = null;
  public profileDisplayName = '';
  public currentPassword = '';
  public nextPassword = '';
  public newApiToken: string | null = null;
  public loading = false;
  public errorMessage: string | null = null;
  public successMessage: string | null = null;

  private readonly roleLabels: Record<UserRole, string> = {
    admin: 'Администратор',
    editor: 'Редактор',
    manager: 'Менеджер',
    viewer: 'Наблюдатель',
  };

  public constructor(
    private userApi: UserManagementApiService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  public async ngOnInit(): Promise<void> {
    await this.loadProfile();
  }

  public get isAdmin(): boolean {
    return this.me?.role === 'admin';
  }

  public get permissionCount(): number {
    return Object.values(this.me?.permissions ?? {}).filter(Boolean).length;
  }

  public get displayName(): string {
    return this.me?.displayName || this.me?.username || 'Профиль';
  }

  public getRoleLabel(role: UserRole): string {
    return this.roleLabels[role] ?? role;
  }

  public formatDate(value: string | null): string {
    if (!value) {
      return '-';
    }

    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  }

  public async saveProfile(): Promise<void> {
    this.clearMessages();
    const response = await this.userApi.updateMe(this.profileDisplayName || null);
    const user = this.unwrapResponse(response);
    if (!user) {
      return;
    }

    this.me = user;
    this.profileDisplayName = user.displayName ?? '';
    this.successMessage = 'Профиль обновлен.';
    this.changeDetector.detectChanges();
  }

  public async changeOwnPassword(): Promise<void> {
    this.clearMessages();
    const response = await this.userApi.changeOwnPassword(this.currentPassword, this.nextPassword);
    if (!this.unwrapResponse(response)) {
      return;
    }

    this.currentPassword = '';
    this.nextPassword = '';
    this.successMessage = 'Ваш пароль обновлен.';
    this.changeDetector.detectChanges();
  }

  public async refreshApiToken(): Promise<void> {
    this.clearMessages();
    const response = await this.userApi.refreshApiToken();
    const result = this.unwrapResponse(response);
    if (!result) {
      return;
    }

    this.newApiToken = result.token;
    this.successMessage = 'API Bearer-token обновлен.';
    await this.loadProfile(false);
  }

  public async copyApiToken(): Promise<void> {
    if (!this.newApiToken || !navigator.clipboard) {
      return;
    }

    await navigator.clipboard.writeText(`Bearer ${this.newApiToken}`);
    this.successMessage = 'Bearer-token скопирован.';
    this.changeDetector.detectChanges();
  }

  private async loadProfile(resetToken = true): Promise<void> {
    this.loading = true;
    this.errorMessage = null;
    if (resetToken) {
      this.newApiToken = null;
    }

    try {
      const user = this.unwrapResponse(await this.userApi.getMe());
      this.me = user;
      this.profileDisplayName = user?.displayName ?? '';
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Не удалось загрузить профиль.';
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  private unwrapResponse<ResponseType>(response: HttpResponse<ResponseType>): ResponseType | null {
    if (response.status === 'failed') {
      this.errorMessage = response.errorMessage;
      return null;
    }

    return response.response;
  }

  private clearMessages(): void {
    this.errorMessage = null;
    this.successMessage = null;
  }
}
